// ============================================================
// 🔐 认证 / 游戏流程 / 选择分支 — 从 dnf.html 提取
// ============================================================

import { S, save, renderTopbar, renderStatusbar, load, defaultState } from '../state.js';
import { CLASSES } from '../data/classes.js';
import { toast } from '../utils.js';
import { checkLevelUp } from '../engine/combat-system.js';
import { startCombat } from '../engine/combat-system.js';
import { addItem, removeItem } from '../engine/economy.js';
import { initQuests, checkDailyReset } from '../engine/character-prog.js';

// ========== 认证模式切换 ==========
export function toggleAuthMode() {
  window.renderAuthForm(window.authMode === 'login' ? 'register' : 'login');
}

// ========== 登出 ==========
export function doLogout() {
  window.clearAuth();
  Object.keys(S).forEach(k => delete S[k]);
  Object.assign(S, defaultState());
  localStorage.removeItem(window.SAVE_KEY || 'dnf_save_v5');
  window.showAuthUI();
}

// ========== 离线模式 ==========
export function startOffline() {
  if(!load()) {
    Object.keys(S).forEach(k => delete S[k]);
    Object.assign(S, defaultState());
  }
  initGameAfterAuth();
}

// ========== 职业选择（创建角色界面）==========
export function selectClass(cid) {
  window._selectedClass = cid;
  document.querySelectorAll('.class-select-item').forEach(el => { el.style.borderColor = '#333'; el.style.background = '#111118'; });
  const el = document.getElementById('cls-' + cid);
  if(el) { el.style.borderColor = 'var(--gold)'; el.style.background = '#2a2a14'; }
  document.getElementById('selectedClass').textContent = `已选：${CLASSES[cid].name}`;
}

// ========== 认证后初始化游戏 ==========
export function initGameAfterAuth() {
  initQuests();
  if(!S.name) {
    Object.keys(S).forEach(k => delete S[k]);
    Object.assign(S, defaultState());
  }
  S.fatigue = Math.min(S.maxFatigue||100, S.fatigue||100);
  if(!S.maxFatigue) S.maxFatigue = 100;
  if(!S.mp && S.mp !== 0) { S.mp = 50; S.maxMp = 50; }
  if(!S.maxMp) S.maxMp = 50;
  if(!S.inventory) S.inventory = [];
  if(!S.equipment) S.equipment = {};
  if(!S.flags) S.flags = {};
  if(!S.regionProgress) S.regionProgress = {};
  if(!S.ownedEquipIds) S.ownedEquipIds = [];
  if(!S.npcBoosts) S.npcBoosts = { atk:0, def:0, spd:0 };
  if(!S.weaponPenalties) S.weaponPenalties = {};
  if(!S.enhanceLevels) S.enhanceLevels = {};

  // 地下城状态恢复：如果存档时有地下城进度，直接回到当前房间
  if(S._dungeonRooms && S._dungeonRooms.length > 0 && S.currentDungeon) {
    S._showModifierBadge = !!S._dungeonModifier;
    S.scene = 'dungeon_room';
  }

  checkDailyReset();
  renderTopbar(); renderStatusbar();

  if(!S.name) {
    window.renderLobby();
  } else if(S.scene === 'lobby') {
    window.renderLobby();
  } else if(S.scene === 'dungeon_room' && S._dungeonRooms) {
    // 恢复地下城探索
    const idx = S._dungeonRoomIndex || 0;
    window.renderDungeonRoom(S._dungeonRooms[idx], idx);
  } else {
    window.renderScene();
  }
}

// ========== 确认新游戏 ==========
export function confirmNewGame() {
  if(confirm('确定要开始新游戏吗？当前存档将被覆盖。')) {
    Object.keys(S).forEach(k => delete S[k]);
    Object.assign(S, defaultState());
    save();
    window.renderChooseClass();
    renderTopbar();
    renderStatusbar();
  }
}

// ========== 选择职业（快速选角）==========
export function pickClass(cls) {
  const c = CLASSES[cls];
  S.cls = cls;
  S.name = c.name;
  S.hp = 100 + c.hpBonus;
  S.maxHp = S.hp;
  S.atk = 10 + c.atkBonus;
  S.def = 5 + c.defBonus;
  S.spd = 5 + c.spdBonus;
  S.mp = 50;
  S.maxMp = 50;
  S.scene = 'elven_start';
  S.fatigue = 156;
  save();
  window.renderScene();
  renderTopbar();
  renderStatusbar();
}

// ========== 返回大厅 ==========
export function goHome() {
  // 副本完成标记仅由 combatWin 处理（击败Boss时），此处不再标记
  const curRegion = window.getCurrentRegionId();
  if(curRegion && S.scene !== 'lobby') S.regionProgress[curRegion] = S.scene;
  S.scene = 'lobby';
  S._hellMode = false;
  S.currentDungeon = null;
  S.currentDungeonDiff = null;
  S._dungeonRooms = null;
  S._dungeonRoomIndex = 0;
  S._dungeonRoomCleared = 0;
  S._dungeonModifier = null;
  S._showModifierBadge = false;
  S._dungeonPendingMonster = null;
  S._dungeonPendingEliteMod = null;
  S._nextDungeonRoomAfterCombat = null;
  S.hp = Math.min(S.maxHp, S.hp + 30);
  if(S.mp) S.mp = Math.min(S.maxMp, S.mp + 10);
  if(S.combat) S.combat = null;
  save();
  window.renderLobby();
  renderStatusbar();
}

// ========== 拥有物品 ==========
export function hasItem(id) {
  if(!S.inventory) return false;
  return S.inventory.some(i=>i.id===id);
}

// ========== 场景选择处理 ==========
export function doChoice(idx) {
  const scene = window.getScene(S.scene);
  if(!scene) return;
  const filtered = scene.choices.filter(c=>{
    if(c.needLv && S.lv < c.needLv) return false;
    if(c.needGold && S.gold < c.needGold) return false;
    if(c.needItem && !hasItem(c.needItem)) return false;
    return true;
  });
  const c = filtered[idx];
  if(!c) return;

  if(c.gold) S.gold = Math.max(0, S.gold + c.gold);
  if(c.exp) { S.exp += c.exp; checkLevelUp(); }
  if(c.item) addItem(c.item);
  if(c.item2) addItem(c.item2);
  if(c.extraItem) addItem(c.extraItem);
  if(c.fatigue) S.fatigue = Math.max(0, S.fatigue - c.fatigue);
  if(c.subHp) { S.hp = Math.max(1, S.hp - c.subHp); toast(`🩸 献祭HP -${c.subHp}`, 2000); }

  // 消耗needItem（通用处理）
  if(c.needItem) {
    removeItem(c.needItem);
    toast('物品已消耗', 1500);
  }

  // 执行分支副作用（声望、标志位等）
  if(c.onEnter) c.onEnter();

  // 特殊动作
  if(c.action === 'home') {
    if(c.gold) { /* already applied */ }
    goHome();
    renderStatusbar();
    return;
  }

  if(c.next) {
    if(c.next === 'lobby') {
      goHome();
      renderStatusbar();
      save();
      return;
    }
    if(c.next.startsWith('battle_')) {
      const battleMap = {
        battle_goblin1:        ['goblin',         'gf_1'],
        battle_goblin2:        ['goblin2',         'gf_1_boss_done'],
        battle_goblin_chief:   ['goblin_chief',    'gf_1_boss_done'],
        battle_zombie:         ['zombie',          'gf_2_done'],
        battle_zombie1:        ['zombie2',         'gf_2_done'],
        battle_tau_beast:      ['tau_beast',       'gf_5_done'],
        battle_tau_chief:      ['tau_chief',       'gf_5_done'],
        battle_thunder_minion: ['thunder_goblin',  'gf_3'],
        battle_thunder_boss:   ['thunder_boss',    'gf_3_done'],
        battle_poison:         ['poison_slime',    'gf_4_done'],
        battle_flame_goblin:   ['flame_goblin',    'gf_6_done'],
        battle_flame_boss:     ['flame_boss',      'gf_6_done'],
        battle_bone_dragon:    ['bone_dragon',     'gf_7_done'],
        battle_cursed_knight:  ['cursed_knight',   'ht_dungeon_1'],
        battle_golem_guard:    ['golem_guard',     'ht_dungeon_done'],
        battle_gbl_zealot:     ['gbl_zealot',      'ht_gbl_1'],
        battle_gbl_bishop:     ['gbl_bishop',      'ht_gbl_done'],
        battle_shadow_mage:    ['shadow_mage',     'ht_magic_1'],
        battle_ice_witch:      ['ice_witch',       'ht_magic_done'],
        battle_dark_sorcerer:  ['dark_sorcerer',   'ht_ice_1'],
        battle_ice_witch_boss: ['ice_witch',       'ht_ice_done'],
        battle_cloud_gargoyle: ['cloud_gargoyle',  'sky_1_done'],
        battle_dragon_soldier: ['dragon_soldier',  'dragon_tower_1'],
        battle_dragon_centurion:['dragon_centurion','dragon_tower_done'],
        battle_storm_hawk:     ['storm_hawk',      'sky_2'],
        battle_angel_fallen:   ['angel_fallen',    'sky_2_done'],
        battle_dark_knight_sky:['dark_knight_sky', 'dark_corridor_1'],
        battle_corridor_mage:  ['corridor_mage',   'dark_corridor_done'],
        battle_sky_dragon:     ['sky_dragon',      'sky_dragon_done'],
        battle_crystal_guardian:['crystal_guardian','sky_dragon_done'],
        battle_arad_demon:     ['arad_demon',      'final_victory'],
        // 暗黑城
        battle_dark_elf_soldier:['dark_elf_soldier','dc_outpost_done'],
        battle_shadow_beast:   ['shadow_beast',    'dc_outpost_done'],
        battle_dark_priest:    ['dark_priest',     'dc_cavern_done'],
        battle_ghost_knight:   ['ghost_knight',    'dc_cavern_done'],
        battle_spider_queen:   ['spider_queen',    'dc_citadel_1'],
        battle_shadow_lord:    ['shadow_lord',     'dc_citadel_done'],
      };
      const entry = battleMap[c.next] || [c.next.replace('battle_',''), 'lobby'];
      const monsterId = entry[0];
      const parentScene = entry[1];

      // 检查是否Boss有多阶段线索
      if(window.BOSS_CLUES[monsterId]) {
        window.showBossClue(monsterId, 0, parentScene);
      } else {
        window.showBattleReveal(monsterId, parentScene);
      }
    } else {
      // 如果在副本中，检查是否离开当前副本边界
      if(S.currentDungeon && !S.currentDungeon.scenes.includes(c.next)) {
        // 到达副本终点 → 回据点（完成标记已由 combatWin 在击败Boss时处理）
        const regionId = window.getCurrentRegionId() || 'forest';
        S.scene = regionId;
        S.currentDungeon = null;
        S.currentDungeonDiff = null;
        S._hellMode = false;
        S.hp = Math.min(S.maxHp, S.hp + 20);
        save();
        window.renderRegionHub(regionId);
      } else {
        // 保存区域进度
        const curRegion = window.getCurrentRegionId();
        if(curRegion) S.regionProgress[curRegion] = c.next;
        S.scene = c.next;
        window.renderScene();
      }
    }
  }

  renderStatusbar();
  renderTopbar();
  save();
}
